import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, Loader2, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

const REVEAL_LINES = [
	'Lendo suas respostas',
	'Cruzando com o perfil dos nossos terapeutas',
	'Calculando a afinidade de cada um',
	'Selecionando os 3 com mais afinidade',
];

/**
 * Duração mínima da animação antes de mostrar o resultado (ms). É proposital:
 * o cálculo é instantâneo, mas a escolha do terapeuta merece o tempo de leitura
 * das etapas. Ajuste aqui para encurtar ou alongar a espera.
 */
export const MATCH_REVEAL_MIN_DURATION_MS = 5000;

interface MatchRevealProps {
	/** true quando a resposta do backend já chegou; a animação termina e chama onDone. */
	ready: boolean;
	onDone: () => void;
}

/** Transição "analisando..." entre o envio das respostas e os 3 terapeutas. */
const MatchReveal = ({ ready, onDone }: MatchRevealProps) => {
	const [completedLines, setCompletedLines] = useState(0);
	const [minElapsed, setMinElapsed] = useState(false);

	useEffect(() => {
		const stepMs =
			MATCH_REVEAL_MIN_DURATION_MS / (REVEAL_LINES.length + 0.5);
		const timers = REVEAL_LINES.map((_, i) =>
			window.setTimeout(() => setCompletedLines(i + 1), stepMs * (i + 1)),
		);
		const minTimer = window.setTimeout(
			() => setMinElapsed(true),
			MATCH_REVEAL_MIN_DURATION_MS,
		);
		return () => {
			timers.forEach((t) => window.clearTimeout(t));
			window.clearTimeout(minTimer);
		};
	}, []);

	useEffect(() => {
		if (ready && minElapsed) {
			const t = window.setTimeout(onDone, 350);
			return () => window.clearTimeout(t);
		}
	}, [ready, minElapsed, onDone]);

	return (
		<div className="flex w-full flex-1 flex-col items-center justify-center gap-10 py-10 text-center">
			<div className="relative flex h-32 w-32 items-center justify-center">
				{[0, 1, 2].map((ring) => (
					<motion.span
						key={ring}
						className="absolute inset-0 rounded-full border-2 border-brand-pink/40"
						initial={{ scale: 0.6, opacity: 0.8 }}
						animate={{ scale: 1.6, opacity: 0 }}
						transition={{
							duration: 2.2,
							repeat: Infinity,
							delay: ring * 0.7,
							ease: 'easeOut',
						}}
					/>
				))}
				<motion.span
					className="relative flex h-20 w-20 items-center justify-center rounded-[1.75rem] bg-stone-900 text-white shadow-xl"
					animate={{ rotate: [0, -6, 6, 0], scale: [1, 1.04, 1] }}
					transition={{
						duration: 2.4,
						repeat: Infinity,
						ease: 'easeInOut',
					}}
				>
					<Sparkles className="h-8 w-8 text-brand-pink" />
				</motion.span>
			</div>

			<div className="space-y-2">
				<h1 className="text-2xl font-black tracking-tight text-stone-900 md:text-3xl">
					Encontrando seu match...
				</h1>
				<p className="text-sm font-medium text-stone-500">
					Estamos procurando quem faz mais sentido para você.
				</p>
			</div>

			<ul className="w-full max-w-sm space-y-3 text-left">
				{REVEAL_LINES.map((line, index) => {
					const done = completedLines > index;
					const active = completedLines === index;
					return (
						<motion.li
							key={line}
							initial={{ opacity: 0, x: -10 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ delay: 0.15 * index }}
							className={cn(
								'flex items-center gap-3 rounded-2xl border bg-white px-4 py-3 transition-colors',
								done
									? 'border-emerald-200'
									: active
									  ? 'border-stone-300'
									  : 'border-stone-100 opacity-60',
							)}
						>
							<span
								className={cn(
									'flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
									done
										? 'bg-emerald-500 text-white'
										: 'bg-stone-100 text-stone-400',
								)}
							>
								<AnimatePresence mode="wait" initial={false}>
									{done ? (
										<motion.span
											key="done"
											initial={{ scale: 0 }}
											animate={{ scale: 1 }}
											transition={{
												type: 'spring',
												stiffness: 400,
												damping: 18,
											}}
										>
											<Check
												className="h-4 w-4"
												strokeWidth={3}
											/>
										</motion.span>
									) : (
										<motion.span
											key="loading"
											animate={{ rotate: 360 }}
											transition={{
												duration: 1,
												repeat: Infinity,
												ease: 'linear',
											}}
										>
											<Loader2 className="h-4 w-4" />
										</motion.span>
									)}
								</AnimatePresence>
							</span>
							<span className="text-sm font-semibold text-stone-700">
								{line}
							</span>
						</motion.li>
					);
				})}
			</ul>
		</div>
	);
};

export { MatchReveal };
